import { useClientPortalAPI } from "@certn/api";
import {
  AddressFormFormikField,
  CheckboxFormikField,
  Divider,
  Grid,
  GridItem,
  Loader,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useMemo, useState } from "react";
import { Trans } from "react-i18next";

import { CreateBillingProfilePageLayout } from "./CreateBillingProfileProfileLayout";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { useSubsidiaryCheckEligibility } from "../../../hooks/useSubsidiaryCheckEligibility";
import { BillingProfileForm } from "../BillingProfileForm";
import {
  initialBillingProfileValues,
  useCreateBillingProfileContext,
} from "../CreateBillingProfileContext";
import { useBillingProfileSchema } from "../useBillingProfileSchema";

export function BillingProfileInformationStep() {
  const { billingProfile, setBillingProfile, goToNextStep } =
    useCreateBillingProfileContext();
  const { client } = useClientAccount();
  const [isAddressSame, setIsAddressSame] = useState(false);

  const [payApplicantNoClientAddress, setPayApplicantNoClientAddress] =
    useState(false);

  const { data: billingProfiles, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/billing-profiles/`,
    { page_size: "all" }
  );

  const profileNames =
    useMemo(
      () => billingProfiles?.results?.map((profile) => profile?.profile_name),
      [billingProfiles]
    ) ?? [];

  const { isEligibleForSubsidiaryCheck } = useSubsidiaryCheckEligibility();

  const schema = useBillingProfileSchema({
    profileNames,
    skipPaymentProfileValidation: isEligibleForSubsidiaryCheck,
  });

  if (isLoading) {
    return <Loader overlay />;
  }

  return (
    <Formik
      initialValues={{
        ...billingProfile,
        customer_account: client?.id || "",
        customer_name:
          billingProfile.customer_name || client?.auth0_display_name || "",
      }}
      onSubmit={(values) => {
        setBillingProfile(values);
        goToNextStep();
      }}
      validationSchema={schema}
    >
      {({ handleSubmit, isSubmitting, dirty, setFieldValue }) => {
        const handlePayerOptionChange = (isApplicant: boolean) => {
          if (isApplicant) {
            if (client?.address) {
              setPayApplicantNoClientAddress(false);
              setIsAddressSame(true);
              setFieldValue("address_same_as_company", true);
              setFieldValue("address", client?.address);
            } else {
              setPayApplicantNoClientAddress(true);
              setIsAddressSame(false);
              setFieldValue("address_same_as_company", false);
              setFieldValue("address", initialBillingProfileValues.address);
            }
          } else {
            setPayApplicantNoClientAddress(false);
            setIsAddressSame(false);
            setFieldValue("address_same_as_company", false);
            setFieldValue("address", initialBillingProfileValues.address);
          }
        };
        return (
          <CreateBillingProfilePageLayout
            onComplete={handleSubmit}
            submitting={isSubmitting}
            formIsDirty={dirty}
          >
            <Grid form>
              <Text style="form/section-heading">
                <Trans>Billing details</Trans>
              </Text>

              <BillingProfileForm
                onPayerOptionChange={handlePayerOptionChange}
                allowedPaymentMethods={
                  client?.allowed_payment_methods
                    ? [...client.allowed_payment_methods]
                    : undefined
                }
              />
              <Divider />

              <Text style="form/section-heading">
                <Trans>Billing address</Trans>
              </Text>
              <GridItem testID="billing-address-checkbox-container">
                {!payApplicantNoClientAddress && (
                  <CheckboxFormikField
                    testID="address-same-as-company"
                    name="address_same_as_company"
                    onChange={(checked) => {
                      setIsAddressSame(checked);
                      setFieldValue(
                        "address",
                        checked
                          ? client?.address
                          : initialBillingProfileValues.address
                      );
                    }}
                  >
                    <Trans>Same as company</Trans>
                    {isAddressSame && (
                      <div className={styles.billingInfoWrapper}>
                        <Text style="input/label">
                          {client?.address?.address_line_1}
                          <br />
                          {client?.address?.locality || "-"},{" "}
                          {client?.address?.administrative_division_1 || "-"}{" "}
                          {client?.address?.country || "-"}
                          <br />
                          {client?.address?.postal_code || "-"}
                        </Text>
                      </div>
                    )}
                  </CheckboxFormikField>
                )}
                {!isAddressSame && (
                  <AddressFormFormikField
                    testID="billing-information-address"
                    name="address"
                  />
                )}
              </GridItem>
            </Grid>
          </CreateBillingProfilePageLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  billingInfoWrapper: css({
    marginTop: "var(--space-2)",
  }),
};
